import { NONE,TOTAL } from '../../../common/utils/constants'

import {
  formatNumberOnUnitMeasure,
  formatUnitMeasure,
  getNameFromDisaggregation,
  formatUnitMult,
  getBadges,
  getUnitMeasureItems,
} from '../../../common/utils/helper'
import { COLOR_MAP } from '../../../common/utils/theme'
import { getChartTitle } from '../common/helper'
import { disaggregations } from '../../../common/utils/enum'


const getStackedBarChartSubtitle = (badges, subtitle) => {
  let badgesDiv =
    badges && badges.length > 0
      ? badges.map(
          (badge) =>
            `<div class="text tui_badge">
        <div>
          <p>${badge}</p>
        </div>
      </div>`
        )
      : []
  badgesDiv = badgesDiv.join(' ')
  return {
    useHTML: true,
    text: `<p class="tui__text_base">${subtitle}</p>
      <div class="tui__badge_container">
        <div class="tui_badge_group">
            ${badgesDiv}
        </div>
      </div>
    `,
    align: 'left',
  }
}

export const generateStackedBarChartOptions = (
  filteredIndicatorData,
  indicatorMetadata,
  disaggregationData,
  selectedFilters,
  filteredAggregateData,
  countryList,
  selectedComparison,
  selectedMaxYear,
  selectedMinYear,
  selectedUnitMeasure,
  unitMeasureName,
  unitMeasureList,
  site
) => {
  const dataSource =
    filteredIndicatorData.length > 0
      ? filteredIndicatorData
      : filteredAggregateData

  const badges = getBadges(
    selectedFilters,
    disaggregationData,
    selectedComparison,
    unitMeasureName
  )

  const shortLabel =
    getUnitMeasureItems(disaggregationData)?.find(
      (item) => item.code === selectedUnitMeasure
    )?.short_label || ''

  const title =
    indicatorMetadata?.admin_metadata?.[site]?.visualization?.display_name ||
    indicatorMetadata?.series_description.name

  let subtitle = []
  let categories = [
    ...new Set(dataSource.flatMap((ind) => ind.data?.xAxis || [])),
  ]
    .filter(
      (year) =>
        parseInt(year) >= parseInt(selectedMinYear) &&
        parseInt(year) <= parseInt(selectedMaxYear)
    )
    .sort((a, b) => parseInt(a) - parseInt(b))

  let series = []
  const legendTracker = new Set()
  let globalColorIndex = 0

  let comparisonData = []

  if (selectedComparison !== NONE) {
    const selectedValues =
      selectedFilters && selectedFilters[selectedComparison]
        ? selectedFilters[selectedComparison].split(',')
        : []
    comparisonData = disaggregationData?.dimensions
      .find((item) => item.field_name === selectedComparison)
      ?.field_value?.filter(
        (item) => item.code !== TOTAL && selectedValues.includes(item.code)
      )
  }

  const getSeriesName = (
    point,
    selectedComparison,
    dimensions,
    defaultName
  ) => {
    if (selectedComparison !== NONE) {
      return (
        point.description ||
        getNameFromDisaggregation(dimensions, selectedComparison, point.code)
      )
    } else {
      return defaultName
    }
  }

  if (filteredIndicatorData && filteredIndicatorData.length) {
    const countryCodes = [
      ...new Set(
        filteredIndicatorData.map((item) => item[disaggregations.RefArea])
      ),
    ]
    if (selectedComparison === NONE || selectedComparison === '') {
      countryCodes.forEach((countryCode) => {
        const countryName =
          countryList?.find((it) => it.id === countryCode)?.name || ''
        subtitle.push(countryName)

        let data = categories.map((year) => {
          let value = null
          const filteredItems = filteredIndicatorData.filter(
            (item) => item[disaggregations.RefArea] === countryCode
          )
          filteredItems.forEach((item) => {
            const idx = item?.data?.xAxis?.findIndex((x) => x === year)
            if (idx !== -1 && item?.data?.yAxis?.[idx] != null) {
              value = parseFloat(item.data.yAxis[idx])
            }
          })
          return { y: value, year: year }
        })

        const seriesName = countryName
        const showInLegend = !legendTracker.has(seriesName)
        legendTracker.add(seriesName)

        series.push({
          name: seriesName,
          data: data,
          stack: countryName,
          stackName: countryName,
          color: COLOR_MAP?.category?.[globalColorIndex++] || undefined,
          showInLegend: showInLegend,
          unitMeasureCode:
            filteredIndicatorData?.[0]?.[disaggregations.UnitMeasure],
        })
      })
    } else {
      countryCodes.forEach((countryCode, countryIndex) => {
        const countryName =
          countryList?.find((it) => it.id === countryCode)?.name || ''
        subtitle.push(countryName)

        comparisonData?.forEach((point, index) => {
          let data = categories.map((year) => {
            let value = null

            const filteredItems = filteredIndicatorData.filter(
              (item) =>
                item[disaggregations.RefArea] === countryCode &&
                item[`${selectedComparison}`] === point.code
            )

            filteredItems.forEach((item) => {
              const idx = item?.data?.xAxis?.findIndex((x) => x === year)
              if (idx !== -1 && item?.data?.yAxis?.[idx] != null) {
                value = parseFloat(item.data.yAxis[idx])
              }
            })

            return {
              y: value,
              year: year,
            }
          })

          const seriesName = getSeriesName(
            point,
            selectedComparison,
            disaggregationData?.dimensions,
            countryName
          )

          const showInLegend = !legendTracker.has(seriesName)
          legendTracker.add(seriesName)
          series.push({
            name: seriesName,
            data: data,
            stack: countryName,
            stackName: countryName,
            color: COLOR_MAP?.category?.[index] || undefined,
            showInLegend: showInLegend,
            unitMeasureCode: filteredIndicatorData.filter(
              (item) => item[`${selectedComparison}`] === point.code
            )?.[0]?.[disaggregations.UnitMeasure],
          })
        })
      })
    }
  }

  if (filteredAggregateData && filteredAggregateData.length) {
    const aggregateCodes = [
      ...new Set(
        filteredAggregateData.map((item) => item[disaggregations.RefArea])
      ),
    ]

    if (selectedComparison === NONE || selectedComparison === '') {
      aggregateCodes.forEach((aggCode, aggIndex) => {
        const aggName = getNameFromDisaggregation(
          disaggregationData?.dimensions,
          disaggregations.RefArea,
          aggCode
        )
        subtitle.push(aggName)

        let data = categories.map((year) => {
          let value = null
          const filteredItems = filteredAggregateData.filter(
            (item) => item[disaggregations.RefArea] === aggCode
          )
          filteredItems.forEach((item) => {
            const idx = item?.data?.xAxis?.findIndex((x) => x === year)
            if (idx !== -1 && item?.data?.yAxis?.[idx] != null) {
              value = parseFloat(item.data.yAxis[idx])
            }
          })
          return { y: value, year: year }
        })

        const seriesName = aggName
        const showInLegend = !legendTracker.has(seriesName)
        legendTracker.add(seriesName)

        series.push({
          name: seriesName,
          data: data,
          stack: aggName,
          stackName: aggName,
          color: COLOR_MAP?.category?.[globalColorIndex++] || undefined,
          showInLegend: showInLegend,
          unitMeasureCode:
            filteredAggregateData?.[0]?.[disaggregations.UnitMeasure],
        })
      })
    } else {
      aggregateCodes.forEach((aggCode, aggIndex) => {
        const aggName = getNameFromDisaggregation(
          disaggregationData?.dimensions,
          disaggregations.RefArea,
          aggCode
        )
        subtitle.push(aggName)

        comparisonData?.forEach((point, index) => {
          let data = categories.map((year) => {
            let value = null

            const filteredItems = filteredAggregateData.filter(
              (item) =>
                item[disaggregations.RefArea] === aggCode &&
                item[`${selectedComparison}`] === point.code
            )

            filteredItems.forEach((item) => {
              const idx = item?.data?.xAxis?.findIndex((x) => x === year)
              if (idx !== -1 && item?.data?.yAxis?.[idx] != null) {
                value = parseFloat(item.data.yAxis[idx])
              }
            })

            return {
              y: value,
              year: year,
            }
          })

          const seriesName = getSeriesName(
            point,
            selectedComparison,
            disaggregationData?.dimensions,
            aggName
          )

          const showInLegend = !legendTracker.has(seriesName)
          legendTracker.add(seriesName)
          series.push({
            name: seriesName,
            data: data,
            stack: aggName,
            stackName: aggName,
            color: COLOR_MAP?.category?.[index] || undefined,
            showInLegend: showInLegend,
            unitMeasureCode: filteredAggregateData.filter(
              (item) => item[`${selectedComparison}`] === point.code
            )?.[0]?.[disaggregations.UnitMeasure],
          })
        })
      })
    }
  }

  const idTracker = new Set()
  series.forEach((s) => {
    if (!idTracker.has(s.name)) {
      s.id = s.name
      idTracker.add(s.name)
    } else {
      s.linkedTo = s.name
    }
  })

  const numStacks = [...new Set(series.map((s) => s.stack))].length

  const stackedColumnChartOptions = {
    chart: {
      type: 'column',
      height: 500,
      scrollablePlotArea: {
        minWidth: categories?.length * 80 * numStacks,
        opacity: 1,
      },
      // spacingRight: 25,
    },
    exporting: {
      enabled: true,
      buttons: {
        contextButton: {
          menuItems: [
            'viewFullscreen',
            'downloadPNG',
            'downloadJPEG',
            'downloadSVG',
            'printChart',
          ],
        },
      },
      height: 600,
      chartOptions: {
        yAxis: {
          stackLabels: {
            enabled: true,
            allowOverlap: true,
            overflow: 'allow',
            crop: false,
            formatter: function () {
              const countryName = this.stack || ''
              const truncatedName = countryName
              return `${formatNumberOnUnitMeasure({
                value: this.total,
                unitMeasure: selectedUnitMeasure,
                decimals: dataSource?.[0]?.[disaggregations.Decimals],
              })} (${truncatedName})`
            },
            rotation: -90,
            // allowOverlap: true,
            style: {
              fontSize: '6px',
            },
          },
        },
      },
    },

    title: getChartTitle(title),
    subtitle: getStackedBarChartSubtitle(
      badges,
      [...new Set(subtitle)]?.join(', ') ?? ''
    ),
    xAxis: {
      categories: categories,
    },
    yAxis: {
      title: {
        text: '',
      },
      stackLabels: {
        enabled: true,
        formatter: function () {
          return `${formatNumberOnUnitMeasure({
            value: this.total,
            unitMeasure: selectedUnitMeasure,
            decimals: dataSource[0]?.[disaggregations.Decimals],
          })}${
            shortLabel
              ? formatUnitMeasure(
                  selectedUnitMeasure,
                  unitMeasureName,
                  shortLabel
                )
              : ''
          }`
        },
      },
      labels: {
        style: { color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.9rem' },
        formatter: function () {
           return `${formatNumberOnUnitMeasure({
            value: this.value,
            unitMeasure: selectedUnitMeasure,
            decimals: dataSource[0]?.[disaggregations.Decimals],
            axis: this.axis,
          })}${
            shortLabel
              ? formatUnitMeasure(
                  selectedUnitMeasure,
                  unitMeasureName,
                  shortLabel
                )
              : ''
          }`
        },
      },
    },
    tooltip: filteredAggregateData?.length
      ? {
          enabled: true,
          useHTML: true,
          formatter() {
            const unitMult = formatUnitMult(
              disaggregationData,
              dataSource?.[0]?.[disaggregations.UnitMult]
            )
            let s = `<strong>${this?.point?.series.userOptions?.stackName},</strong>`
            s += ` ${this.x} <br/><span style="color:${
              this?.point.color
            }">\u25CF</span> ${
              this?.point.series.name
            } : <b> ${formatNumberOnUnitMeasure({
              value: this.y,
              unitMeasure:
                selectedUnitMeasure ||
                this.point?.series?.userOptions?.unitMeasureCode,
              decimals: dataSource?.[0]?.[disaggregations.Decimals],
              isCommaSeparatedFormat: true,
            })}${unitMult ? ` ${unitMult}` : ''}${formatUnitMeasure(
              selectedUnitMeasure ||
                this.point?.series?.userOptions?.unitMeasureCode,
              unitMeasureName ||
                unitMeasureList.find(
                  (item) =>
                    item.code ===
                    this.point?.series?.userOptions?.unitMeasureCode
                )?.description ||
                '',
              shortLabel ||
                unitMeasureList.find(
                  (item) =>
                    item.code ===
                    this.point?.series?.userOptions?.unitMeasureCode
                )?.short_label ||
                ''
            )}</b>`
            return s
          },
        } // Disable tooltip when aggregate data exists
      : {
          shared: true,
          useHTML: true,
          formatter() {
            const unitMult = formatUnitMult(
              disaggregationData,
              dataSource?.[0]?.[disaggregations.UnitMult]
            )
            let s = `<strong>${this?.point?.series.userOptions?.stackName}, ${this.x}</strong>`
            this.points.forEach((point) => {
              s += `<br/><span style="color:${point.color}">\u25CF</span> ${
                point.series.name
              }: <b> ${formatNumberOnUnitMeasure({
                value: point.y,
                unitMeasure:
                  selectedUnitMeasure ||
                  point?.series?.userOptions?.unitMeasureCode,
                decimals: dataSource?.[0]?.[disaggregations.Decimals],
                isCommaSeparatedFormat: true,
              })}${unitMult ? ` ${unitMult}` : ''}${formatUnitMeasure(
                selectedUnitMeasure ||
                  point?.series?.userOptions?.unitMeasureCode,
                unitMeasureName ||
                  unitMeasureList.find(
                    (item) =>
                      item.code === point?.series?.userOptions?.unitMeasureCode
                  )?.description ||
                  '',
                shortLabel ||
                  unitMeasureList.find(
                    (item) =>
                      item.code === point?.series?.userOptions?.unitMeasureCode
                  )?.short_label ||
                  ''
              )}</b>`
            })
            return s
          },
        },

    plotOptions: {
      series: {
        stacking: 'normal',
        pointPadding: 0.1,
        groupPadding: 0.2,
        pointWidth: 45,
      },
      column: {
        dataLabels: {
          enabled: false,
          formatter: function () {
            return this.y === 0 ? this.y : null
          },
        },
      },
    },
    credits: {
      enabled: false,
    },
    series: series,
  }

  return stackedColumnChartOptions
}
